import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, Lock, Upload, FileText, Trash2 } from "lucide-react";

async function toBase64(file) {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user.name || "", mobile: user.mobile || "", course: user.course || "",
    batch: user.batch || "", trainer: user.trainer || "",
    parent_name: user.parent_name || "", parent_mobile: user.parent_mobile || "", address: user.address || "",
  });
  const [pwd, setPwd] = useState({ old_password: "", new_password: "", confirm: "" });
  const [docs, setDocs] = useState([]);
  const [docOpen, setDocOpen] = useState(false);
  const [docForm, setDocForm] = useState({ title: "", file_name: "", file_data: "" });

  useEffect(() => { api.get("/documents").then(({ data }) => setDocs(data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/students/${user.id}`, form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) return toast.error("Passwords do not match");
    try {
      const { data } = await api.post("/auth/change-password", { old_password: pwd.old_password, new_password: pwd.new_password });
      if (data?.token) localStorage.setItem("qie_token", data.token);
      toast.success("Password changed");
      setPwd({ old_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const onDocFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const data = await toBase64(f);
    setDocForm({ ...docForm, file_name: f.name, file_data: data });
  };

  const uploadDoc = async (e) => {
    e.preventDefault();
    try {
      await api.post("/documents", docForm);
      toast.success("Document uploaded");
      setDocOpen(false); setDocForm({ title: "", file_name: "", file_data: "" });
      const { data } = await api.get("/documents"); setDocs(data);
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const delDoc = async (id) => {
    if (!window.confirm("Delete document?")) return;
    await api.delete(`/documents/${id}`);
    const { data } = await api.get("/documents"); setDocs(data);
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">My Profile</div>
        <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Personal Details</h1>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 flex items-center gap-5">
        <div className="w-20 h-20 rounded-full bg-[#0F2E4A] text-white grid place-items-center font-heading text-3xl font-bold">
          {user.name?.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-2xl font-bold text-[#0F172A]">{user.name}</h3>
          <div className="text-sm text-[#475569]">{user.email}</div>
          <div className="text-xs text-[#64748B] mt-1 font-mono-plex">{user.student_id || user.role.toUpperCase()}</div>
        </div>
      </div>

      <Tabs defaultValue="personal">
        <TabsList>
          <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
          <TabsTrigger value="parent" data-testid="tab-parent">Parent / Guardian</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Change Password</TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="mt-6">
          <form onSubmit={save} className="bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <F label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="p-name" />
              <F label="Mobile Number" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} testid="p-mobile" />
              <F label="Course" value={form.course} onChange={(v) => setForm({ ...form, course: v })} testid="p-course" disabled={user.role === "student"} />
              <F label="Batch" value={form.batch} onChange={(v) => setForm({ ...form, batch: v })} testid="p-batch" disabled={user.role === "student"} />
              <F label="Trainer" value={form.trainer} onChange={(v) => setForm({ ...form, trainer: v })} testid="p-trainer" disabled={user.role === "student"} />
              <div>
                <Label className="text-xs font-semibold text-[#475569]">Address</Label>
                <Textarea data-testid="p-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <Button type="submit" data-testid="save-profile" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">
              <Save className="w-4 h-4 mr-1.5" /> Save Changes
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="parent" className="mt-6">
          <form onSubmit={save} className="bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <F label="Parent / Guardian Name" value={form.parent_name} onChange={(v) => setForm({ ...form, parent_name: v })} testid="p-pname" />
              <F label="Parent Mobile" value={form.parent_mobile} onChange={(v) => setForm({ ...form, parent_mobile: v })} testid="p-pmobile" />
            </div>
            <Button type="submit" className="bg-[#0F2E4A] hover:bg-[#174169] text-white" data-testid="save-parent">
              <Save className="w-4 h-4 mr-1.5" /> Save
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="documents" className="mt-6 space-y-4">
          <Dialog open={docOpen} onOpenChange={setDocOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-doc-button" className="bg-[#059669] hover:bg-[#047857] text-white">
                <Upload className="w-4 h-4 mr-1.5" /> Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
              <form onSubmit={uploadDoc} className="space-y-3">
                <F label="Title *" value={docForm.title} onChange={(v) => setDocForm({ ...docForm, title: v })} req testid="doc-title" />
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">File *</Label>
                  <Input data-testid="doc-file" type="file" onChange={onDocFile} required className="mt-1.5" />
                </div>
                <DialogFooter><Button type="submit" data-testid="save-doc" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">Upload</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {docs.length === 0 && <div className="text-sm text-[#64748B] col-span-2">No documents uploaded.</div>}
            {docs.map((d) => (
              <div key={d.id} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center gap-3">
                <FileText className="w-6 h-6 text-[#0F2E4A]" />
                <div className="flex-1">
                  <div className="font-medium text-[#0F172A]">{d.title}</div>
                  <div className="text-xs text-[#64748B]">{d.file_name}</div>
                </div>
                <button onClick={() => delDoc(d.id)} data-testid={`del-doc-${d.id}`} className="p-2 hover:bg-[#FEF2F2] rounded">
                  <Trash2 className="w-4 h-4 text-[#DC2626]" />
                </button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="security" className="mt-6">
          <form onSubmit={changePwd} className="bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4 max-w-md">
            <F label="Current Password *" type="password" value={pwd.old_password} onChange={(v) => setPwd({ ...pwd, old_password: v })} req testid="pwd-old" />
            <F label="New Password *" type="password" value={pwd.new_password} onChange={(v) => setPwd({ ...pwd, new_password: v })} req testid="pwd-new" />
            <F label="Confirm Password *" type="password" value={pwd.confirm} onChange={(v) => setPwd({ ...pwd, confirm: v })} req testid="pwd-confirm" />
            <Button type="submit" data-testid="save-pwd" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">
              <Lock className="w-4 h-4 mr-1.5" /> Update Password
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function F({ label, value, onChange, type = "text", req, testid, disabled }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#475569]">{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={req} disabled={disabled} className="mt-1.5" />
    </div>
  );
}
